import api from '../api/client';

export interface EmailCampaign {
  id: string;
  subject: string;
  message: string;
  send_to_all: boolean;
  departments: string[];
  departments_names: string[];
  employees: string[];
  employees_names: string[];
  status: 'draft' | 'sending' | 'sent' | 'failed';
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  created_by: string;
  created_by_name: string;
  created_at: string;
  sent_at: string | null;
  attachments: EmailAttachment[];
  logs?: EmailLog[];
}

export interface EmailAttachment {
  id: string;
  filename: string;
  file: string;
  file_size: number;
  created_at: string;
}

export interface EmailLog {
  id: string;
  employee: string;
  employee_name: string;
  employee_email: string;
  status: 'pending' | 'sent' | 'failed';
  error_message: string;
  created_at: string;
  sent_at: string | null;
}

export interface EmailRecipient {
  id: string;
  full_name: string;
  email: string;
  department: string;
  has_email: boolean;
}

export interface PreviewRecipientsResponse {
  total: number;
  with_email: number;
  without_email: number;
  recipients: EmailRecipient[];
}

export interface SendEmailResponse {
  message: string;
  sent_count: number;
  failed_count: number;
  total: number;
}

// ==================== RECEIVED EMAIL INTERFACES ====================

export interface ReceivedEmailAttachment {
  id: string;
  filename: string;
  file_path: string;
  file_size: number;
  content_type: string;
}

export interface ReceivedEmail {
  id: string;
  message_id: string;
  from_email: string;
  from_name: string;
  to_email: string;
  subject: string;
  body_text: string;
  body_html: string;
  received_at: string;
  is_read: boolean;
  is_starred: boolean;
  has_attachments: boolean;
  attachments: ReceivedEmailAttachment[];
}

export interface ReceivedEmailListItem {
  id: string;
  from_email: string;
  from_name: string;
  subject: string;
  received_at: string;
  is_read: boolean;
  is_starred: boolean;
  has_attachments: boolean;
}

// ==================== EMAIL LOGS (ACCUSÉS DE RÉCEPTION) ====================

export interface EmailLog {
  id: string;
  campaign: string;
  campaign_subject: string;
  employee: string;
  employee_name: string;
  employee_email: string;
  status: 'pending' | 'sent' | 'failed';
  error_message: string;
  created_at: string;
  sent_at: string | null;
}

export interface EmailLogResponse {
  campaign: {
    id: string;
    subject: string;
  };
  statistics: {
    total: number;
    sent: number;
    failed: number;
    pending: number;
  };
  logs: EmailLog[];
}

// ==================== EMAIL SERVICE ====================


const emailService = {
  /**
   * Récupérer toutes les campagnes email
   */
  async getAll() {
    const response = await api.get<EmailCampaign[]>('/employees/email_campaigns/');
    return response.data;
  },

  /**
   * Récupérer une campagne par ID
   */
  async getById(id: string) {
    const response = await api.get<EmailCampaign>(`/employees/email_campaigns/${id}/`);
    return response.data;
  },

  /**
   * Créer une nouvelle campagne
   */
  async create(data: {
    subject: string;
    message: string;
    send_to_all: boolean;
    departments?: string[];
    employees?: string[];
    attachment_files?: File[];
  }) {
    const formData = new FormData();
    formData.append('subject', data.subject);
    formData.append('message', data.message);
    formData.append('send_to_all', String(data.send_to_all));

    if (data.departments && data.departments.length > 0) {
      data.departments.forEach((dept) => {
        formData.append('departments', dept);
      });
    }

    if (data.employees && data.employees.length > 0) {
      data.employees.forEach((emp) => {
        formData.append('employees', emp);
      });
    }

    if (data.attachment_files && data.attachment_files.length > 0) {
      data.attachment_files.forEach((file) => {
        formData.append('attachment_files', file);
      });
    }

    const response = await api.post<EmailCampaign>('/employees/email_campaigns/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Mettre à jour une campagne
   */
  async update(id: string, data: Partial<EmailCampaign>) {
    const response = await api.patch<EmailCampaign>(`/employees/email_campaigns/${id}/`, data);
    return response.data;
  },

  /**
   * Supprimer une campagne
   */
  async delete(id: string) {
    await api.delete(`/employees/email_campaigns/${id}/`);
  },

  /**
   * Prévisualiser les destinataires
   */
  async previewRecipients(id: string) {
    const response = await api.get<PreviewRecipientsResponse>(
      `/employees/email_campaigns/${id}/preview_recipients/`
    );
    return response.data;
  },

  /**
   * Envoyer un email de test
   */
  async sendTest(id: string) {
    const response = await api.post<{ message: string }>(
      `/employees/email_campaigns/${id}/test_send/`
    );
    return response.data;
  },

  /**
   * Envoyer la campagne
   */
  async send(id: string) {
    const response = await api.post<SendEmailResponse>(`/employees/email_campaigns/${id}/send/`);
    return response.data;
  },

  // ==================== RECEIVED EMAILS ====================

  /**
   * Récupérer tous les emails reçus (boîte de réception)
   */
  async getInbox() {
    const response = await api.get<ReceivedEmailListItem[]>('/employees/received_emails/');
    return response.data;
  },

  /**
   * Récupérer un email reçu par ID avec détails complets
   */
  async getReceivedEmailById(id: string) {
    const response = await api.get<ReceivedEmail>(`/employees/received_emails/${id}/`);
    return response.data;
  },

  /**
   * Récupérer de nouveaux emails depuis le serveur IMAP
   */
  async fetchNewEmails(limit: number = 50, onlyUnread: boolean = false) {
    const response = await api.post<{ message: string; fetched: number }>(
      '/employees/received_emails/fetch/',
      { limit, only_unread: onlyUnread }
    );
    return response.data;
  },

  /**
   * Marquer un email comme lu
   */
  async markAsRead(id: string) {
    const response = await api.post<ReceivedEmail>(`/employees/received_emails/${id}/mark_read/`);
    return response.data;
  },

  /**
   * Marquer un email comme non lu
   */
  async markAsUnread(id: string) {
    const response = await api.post<ReceivedEmail>(`/employees/received_emails/${id}/mark_unread/`);
    return response.data;
  },

  /**
   * Basculer le statut étoilé d'un email
   */
  async toggleStar(id: string) {
    const response = await api.post<ReceivedEmail>(`/employees/received_emails/${id}/toggle_star/`);
    return response.data;
  },

  /**
   * Supprimer un email reçu
   */
  async deleteReceivedEmail(id: string) {
    await api.delete(`/employees/received_emails/${id}/`);
  },

  // ==================== EMAIL LOGS ====================

  /**
   * Récupérer les logs d'email d'une campagne avec statistiques
   */
  async getLogsByCampaign(campaignId: string) {
    const response = await api.get<EmailLogResponse>(
      `/employees/email_logs/by_campaign/?campaign_id=${campaignId}`
    );
    return response.data;
  },

  /**
   * Récupérer tous les logs avec filtres optionnels
   */
  async getAllLogs(campaignId?: string, status?: string) {
    const params = new URLSearchParams();
    if (campaignId) params.append('campaign_id', campaignId);
    if (status) params.append('status', status);
    
    const response = await api.get<EmailLog[]>(
      `/employees/email_logs/?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Supprimer un log d'email
   */
  async deleteEmailLog(id: string) {
    await api.delete(`/employees/email_logs/${id}/`);
  },
};

export default emailService;
